import React, { useState } from 'react';
import axios from 'axios';
import { FiSearch, FiX } from 'react-icons/fi';
import {
    singaporeNowDatetimeLocal,
    toSingaporeDatetimeLocalValue,
} from '../utils/singaporeTime';
import { getViewObject } from '../utils/reviewLabels';

const inputClass = 'mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-blue-700';
const COMMENT_LENGTH_WARN = 1020;

const formatGodMemberLabel = (m) => {
    if (!m) return '';
    const lastOld = m.oldNames?.length ? m.oldNames[m.oldNames.length - 1] : null;
    const name = lastOld ? `${lastOld}(${m.godName || ''})` : (m.godName || '');
    return `${m.godID || ''} - ${name} - ${m.webID || ''}`.trim() || m._id;
};

const formatNonMemberLabel = (m) => {
    if (!m) return '';
    return `${m.name || '—'} - ${m.webID || ''}`.trim() || m._id;
};

const formatConsumerLabel = (c) => {
    if (!c) return '';
    return c.displayName || c.username || c.useremail || c._id;
};

const GodReviewModal = ({ initialReview, onClose, onSuccess, getAuthHeaders }) => {
    const isEdit = Boolean(initialReview?._id);
    const initialSubject = initialReview?.reviewFor;
    const initialConsumer = initialReview?.reviewBy;

    const [formData, setFormData] = useState(() => {
        const views = getViewObject(initialReview);
        return {
        reviewForModel: initialReview?.reviewForModel || 'godMember',
        reviewFor: initialSubject?._id || initialSubject || '',
        slgWebView: views.slgWebView,
        reviewWebView: views.reviewWebView,
        otherView: views.otherView,
        lastPostDate: toSingaporeDatetimeLocalValue(initialReview?.lastPostDate),
        publishDate: toSingaporeDatetimeLocalValue(initialReview?.publishDate) || singaporeNowDatetimeLocal(),
        reviewBy: initialConsumer?._id || initialConsumer || '',
        reviewByDisplayName: initialReview?.reviewByDisplayName ?? '',
        reviewComment: initialReview?.reviewComment ?? '',
        rating: initialReview?.rating ?? 5,
        hidden: Boolean(initialReview?.hidden),
    };
    });
    const [selectedSubject, setSelectedSubject] = useState(
        initialSubject && typeof initialSubject === 'object' ? initialSubject : null
    );
    const [selectedConsumer, setSelectedConsumer] = useState(
        initialConsumer && typeof initialConsumer === 'object' ? initialConsumer : null
    );
    const [subjectSearch, setSubjectSearch] = useState('');
    const [subjectResults, setSubjectResults] = useState([]);
    const [consumerSearch, setConsumerSearch] = useState('');
    const [consumerResults, setConsumerResults] = useState([]);
    const [showSubjectSearch, setShowSubjectSearch] = useState(false);
    const [showConsumerSearch, setShowConsumerSearch] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const handleModelChange = (e) => {
        setFormData((p) => ({ ...p, reviewForModel: e.target.value, reviewFor: '' }));
        setSelectedSubject(null);
        setSubjectSearch('');
        setSubjectResults([]);
    };

    const searchSubject = async (term) => {
        if (!term.trim()) {
            setSubjectResults([]);
            return;
        }
        const base = process.env.REACT_APP_API_URL;
        const url = formData.reviewForModel === 'godMember'
            ? `${base}/api/godmembers/search?term=${encodeURIComponent(term.trim())}`
            : `${base}/api/nonmembers?search=${encodeURIComponent(term)}&limit=30`;
        try {
            const res = await axios.get(url, { headers: getAuthHeaders() });
            if (res.data.success) setSubjectResults(res.data.data || []);
        } catch {
            setSubjectResults([]);
        }
    };

    const searchConsumer = async (term) => {
        if (!term.trim()) {
            setConsumerResults([]);
            return;
        }
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/consumers?search=${encodeURIComponent(term)}&limit=30`,
                { headers: getAuthHeaders() }
            );
            if (res.data.success) setConsumerResults(res.data.data || []);
        } catch {
            setConsumerResults([]);
        }
    };

    const selectSubject = (item) => {
        setSelectedSubject(item);
        setFormData((p) => ({ ...p, reviewFor: item._id }));
        setSubjectSearch('');
        setSubjectResults([]);
        setShowSubjectSearch(false);
    };

    const selectConsumer = (item) => {
        setSelectedConsumer(item);
        setFormData((p) => ({
            ...p,
            reviewBy: item._id,
            reviewByDisplayName: p.reviewByDisplayName || item.username || item.useremail || ''
        }));
        setConsumerSearch('');
        setConsumerResults([]);
        setShowConsumerSearch(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((p) => ({
            ...p,
            [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        if (!formData.reviewFor) {
            setFormError('Please select who this review is for.');
            return;
        }
        if (!formData.reviewBy) {
            setFormError('Please select a consumer (reviewer).');
            return;
        }
        const rating = parseInt(formData.rating, 10);
        if (!rating || rating < 1 || rating > 5) {
            setFormError('Rating must be 1–5.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                reviewFor: formData.reviewFor,
                reviewForModel: formData.reviewForModel,
                viewObject: {
                    slgWebView: Number(formData.slgWebView) || 0,
                    reviewWebView: Number(formData.reviewWebView) || 0,
                    otherView: Number(formData.otherView) || 0,
                },
                reviewBy: formData.reviewBy,
                reviewByDisplayName: formData.reviewByDisplayName,
                reviewComment: formData.reviewComment,
                rating,
                hidden: Boolean(formData.hidden),
                lastPostDate: formData.lastPostDate || null,
                publishDate: formData.publishDate || undefined
            };
            const url = `${process.env.REACT_APP_API_URL}/api/godreviews`;
            if (isEdit) {
                await axios.put(`${url}/${initialReview._id}`, payload, { headers: getAuthHeaders() });
            } else {
                await axios.post(url, payload, { headers: getAuthHeaders() });
            }
            onSuccess();
        } catch (err) {
            setFormError(err.response?.data?.error || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const subjectLabel = selectedSubject
        ? (formData.reviewForModel === 'godMember'
            ? formatGodMemberLabel(selectedSubject)
            : formatNonMemberLabel(selectedSubject))
        : '';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold">{isEdit ? 'Edit Review' : 'Create Review'}</h2>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
                        <FiX size={20} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-4 overflow-y-auto flex-1">
                    {formError && <div className="mb-3 p-2 bg-red-50 text-red-700 rounded text-sm">{formError}</div>}
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Review for type *</label>
                            <select
                                name="reviewForModel"
                                value={formData.reviewForModel}
                                onChange={handleModelChange}
                                className={inputClass}
                            >
                                <option value="godMember">God Member</option>
                                <option value="NonMember">Non-Member</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Review for *</label>
                            {selectedSubject ? (
                                <div className="flex items-center gap-2 py-2 px-3 border border-gray-300 rounded-md mt-1 min-h-[42px]">
                                    <span className="text-sm truncate text-blue-700">{subjectLabel}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedSubject(null);
                                            setFormData((p) => ({ ...p, reviewFor: '' }));
                                        }}
                                        className="text-gray-400 hover:text-gray-600 shrink-0"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative mt-1">
                                    <input
                                        type="text"
                                        value={subjectSearch}
                                        onChange={(e) => {
                                            setSubjectSearch(e.target.value);
                                            searchSubject(e.target.value);
                                        }}
                                        onFocus={() => setShowSubjectSearch(true)}
                                        placeholder={formData.reviewForModel === 'godMember' ? 'Search godID, webID, godName, oldName...' : 'Search name, webID...'}
                                        className="block w-full border border-gray-300 rounded-md px-3 py-2 pr-9"
                                    />
                                    <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    {showSubjectSearch && subjectResults.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                                            {subjectResults.map((item) => (
                                                <button
                                                    key={item._id}
                                                    type="button"
                                                    className="w-full text-left p-2 hover:bg-gray-100 text-sm"
                                                    onClick={() => selectSubject(item)}
                                                >
                                                    {formData.reviewForModel === 'godMember'
                                                        ? formatGodMemberLabel(item)
                                                        : formatNonMemberLabel(item)}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Consumer (reviewBy) *</label>
                            {selectedConsumer ? (
                                <div className="flex items-center gap-2 py-2 px-3 border border-gray-300 rounded-md mt-1 min-h-[42px]">
                                    <span className="text-sm truncate text-blue-700">{formatConsumerLabel(selectedConsumer)}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedConsumer(null);
                                            setFormData((p) => ({ ...p, reviewBy: '' }));
                                        }}
                                        className="text-gray-400 hover:text-gray-600 shrink-0"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative mt-1">
                                    <input
                                        type="text"
                                        value={consumerSearch}
                                        onChange={(e) => {
                                            setConsumerSearch(e.target.value);
                                            searchConsumer(e.target.value);
                                        }}
                                        onFocus={() => setShowConsumerSearch(true)}
                                        placeholder="Search username or email..."
                                        className="block w-full border border-gray-300 rounded-md px-3 py-2 pr-9"
                                    />
                                    <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    {showConsumerSearch && consumerResults.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                                            {consumerResults.map((item) => (
                                                <button
                                                    key={item._id}
                                                    type="button"
                                                    className="w-full text-left p-2 hover:bg-gray-100 text-sm"
                                                    onClick={() => selectConsumer(item)}
                                                >
                                                    {formatConsumerLabel(item)}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Reviewer display name</label>
                            <input name="reviewByDisplayName" value={formData.reviewByDisplayName} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Rating * (1–5)</label>
                            <select name="rating" value={formData.rating} onChange={handleChange} className={inputClass} required>
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Comment (HTML allowed)</label>
                            <textarea name="reviewComment" value={formData.reviewComment} onChange={handleChange} rows={5} className={inputClass} />
                            <p
                                className={`mt-1 text-sm ${
                                    (formData.reviewComment?.length ?? 0) > COMMENT_LENGTH_WARN
                                        ? 'text-red-600 font-medium'
                                        : 'text-gray-500'
                                }`}
                            >
                                Character Count: {formData.reviewComment?.length ?? 0} Character
                            </p>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">SLG web views</label>
                                <input type="number" name="slgWebView" min={0} value={formData.slgWebView} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Review web views</label>
                                <input type="number" name="reviewWebView" min={0} value={formData.reviewWebView} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Other views</label>
                                <input type="number" name="otherView" min={0} value={formData.otherView} onChange={handleChange} className={inputClass} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Publish date (SG)</label>
                                <input type="datetime-local" name="publishDate" value={formData.publishDate} onChange={handleChange} className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Last post date (SG)</label>
                                <input type="datetime-local" name="lastPostDate" value={formData.lastPostDate} onChange={handleChange} className={inputClass} />
                            </div>
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input type="checkbox" name="hidden" checked={formData.hidden} onChange={handleChange} />
                            Hidden
                        </label>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GodReviewModal;
